import React from 'react';
import styled from 'styled-components';
import Button from './Button';
import { Link } from 'react-router-dom';

interface HeroProps {
  title: string;
  subtitle: string;
  ctaText?: string;
  ctaLink?: string;
  ctaSecondaryText?: string;
  ctaSecondaryLink?: string;
  backgroundImage?: string;
  badges?: string[];
}

const HeroContainer = styled.section`
  position: relative;
  padding: 4rem 1.25rem;
  background: var(--white);
  color: var(--dark);
  overflow: hidden;
  
  @media (min-width: 768px) {
    padding: 7rem 2rem;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 992px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const TextContent = styled.div`
  text-align: left;
  margin-bottom: 3rem;
  
  @media (min-width: 992px) {
    flex: 1;
    max-width: 50%;
    margin-bottom: 0;
    padding-right: 2rem;
  }
`;

const ImageContent = styled.div`
  @media (min-width: 992px) {
    flex: 1;
    max-width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const HeroBadges = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
  
  @media (max-width: 767px) {
    gap: 0.6rem;
    margin-bottom: 1.5rem;
  }
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Badge = styled.span<{ index: number }>`
  background-color: ${props => 
    props.index === 0 ? 'rgba(40, 167, 69, 0.15)' : 
    props.index === 1 ? 'rgba(0, 123, 255, 0.15)' : 
    'rgba(255, 193, 7, 0.15)'
  };
  padding: 0.45rem 1rem;
  border-radius: 2rem;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  color: ${props => 
    props.index === 0 ? '#1e7e34' : 
    props.index === 1 ? '#0056b3' : 
    '#d39e00'
  };
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      rgba(255, 255, 255, 0.2),
      rgba(255, 255, 255, 0)
    );
    transform: rotate(30deg);
    pointer-events: none;
  }
  
  @media (min-width: 768px) {
    padding: 0.5rem 1.1rem;
    font-size: 0.9rem;
  }
  
  &::before {
    content: '';
    display: inline-block;
    width: 0.5rem;
    height: 0.5rem;
    background-color: ${props => 
      props.index === 0 ? 'var(--green)' : 
      props.index === 1 ? 'var(--blue)' : 
      'var(--yellow)'
    };
    border-radius: 50%;
    margin-right: 0.5rem;
    box-shadow: 0 0 0 2px ${props => 
      props.index === 0 ? 'rgba(40, 167, 69, 0.3)' : 
      props.index === 1 ? 'rgba(0, 123, 255, 0.3)' : 
      'rgba(255, 193, 7, 0.3)'
    };
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    background-color: ${props => 
      props.index === 0 ? 'rgba(40, 167, 69, 0.2)' : 
      props.index === 1 ? 'rgba(0, 123, 255, 0.2)' : 
      'rgba(255, 193, 7, 0.2)'
    };
  }
`;

const HeroTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  line-height: 1.2;
  color: var(--dark);
  
  @media (min-width: 768px) {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
  }
  
  strong {
    color: var(--blue);
    position: relative;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.1rem;
  margin-bottom: 1.75rem;
  color: var(--gray);
  line-height: 1.5;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    line-height: 1.6;
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
  
  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
    gap: 0.75rem;
    
    a {
      width: 100%;
      text-align: center;
      justify-content: center;
    }
  }
`;

const GuaranteeText = styled.div`
  font-size: 0.9rem;
  color: var(--gray);
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
    color: var(--green);
  }
`;

const formatTitle = (title: string) => {
  // Resalta la palabra "barata" o "económica" en el título
  return title.replace(/(barata|económica)/gi, '<strong>$1</strong>');
};

const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  ctaText = 'Solicita presupuesto',
  ctaLink = '/contacto',
  ctaSecondaryText = 'Ver servicios',
  ctaSecondaryLink = '/servicios',
  backgroundImage,
  badges = ['Dominio gratis', 'Creación rápida', 'Soporte 24/7'],
}) => {
  return (
    <HeroContainer>
      <HeroContent>
        <TextContent>
          <HeroBadges>
            {badges.map((badge, index) => (
              <Badge key={index} index={index}>{badge}</Badge>
            ))}
          </HeroBadges>
          <HeroTitle dangerouslySetInnerHTML={{ __html: formatTitle(title) }} />
          <HeroSubtitle>{subtitle}</HeroSubtitle>
          <HeroActions>
            <Button
              as={Link}
              to={ctaLink}
              size="large"
            >
              {ctaText}
            </Button>
            <Button
              as={Link}
              to={ctaSecondaryLink}
              variant="outline"
              size="large"
            >
              {ctaSecondaryText}
            </Button>
          </HeroActions>
          <GuaranteeText>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Garantía de devolución de 30 días
          </GuaranteeText>
        </TextContent>
        <ImageContent>
          <img src={backgroundImage || "/fotos generales para el proyecto/hero-image.webp"} alt="Web a Bajo Coste" />
        </ImageContent>
      </HeroContent>
    </HeroContainer>
  );
};

export default Hero; 